import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWorkkardTabCountRequest,
  fetchWorkkardListingDataRequest,
  selectWorkkardListingData,
  selectWorkkardListingTabs,
  selectPagingModel,
  selectIsLoading as selectIsWorkkardListingLoading,
} from "./slice";
import CardListing from "@/components/CardListing";
import { ListingViewConstants } from "@/common/constants";
import LoaderComponent from "@/components/Loader";
import { showDialogueBox } from "@/components/DialogueBox/actions";
import { debounce } from "lodash";
import queryString from "query-string";
import ErrorScreen from "@/ErrorScreens";
import { useLegacyHistory } from "../../router/hooks";
import { useModules } from "@/utils/modules";
import useLiveRefresh from '../../utils/useLiveRefresh';

const WorkkardListing = (props) => {
  const dispatch = useDispatch();
  const { location, history } = useLegacyHistory();
  const { isModuleEnabled } = useModules();
  const sessionContext = useSelector((state) => state.sessionContextOidc);
  const userView = sessionContext?.userView;
  const workkardListingTabs = useSelector(selectWorkkardListingTabs);
  const workkardListingData = useSelector(selectWorkkardListingData);
  const isLoading = useSelector((state) => state.isLoading);
  const isFetchingListing = useSelector(selectIsWorkkardListingLoading);
  const pagingModel = useSelector(selectPagingModel);
  const dialogueBox = useSelector((state) => state.dialogueBox);
  const appError = useSelector((state) => state.appError);

  const resolveWorkkardListingView = (isGridPref) =>
    isGridPref || !isModuleEnabled("workkards.default_grid_view")
      ? ListingViewConstants.ListView
      : ListingViewConstants.CardView;

  const [selectedTab, setSelectedTab] = React.useState("open");
  const [view, setView] = React.useState(
    resolveWorkkardListingView(sessionContext?.isWorkkardGridView),
  );
  const [searchText, setSearchText] = React.useState("");
  const [sortBy, setSortBy] = React.useState(1);
  const [isSortDesc, setIsSortDesc] = React.useState(true);

  const stateRef = React.useRef({});
  stateRef.current = { selectedTab, view, searchText, sortBy, isSortDesc };

  const fetchListingData = React.useCallback(({ silent = false } = {}) => {
    const { selectedTab, view, searchText, sortBy, isSortDesc } =
      stateRef.current;
    const trimmedQuery = searchText.toLocaleLowerCase().trim();
    const workkardListingPayload = {
      key: trimmedQuery,
      skip: 0,
      rows: sessionContext?.IsWorkkardGridView ? 50 : 100,
      page: 1,
      status: selectedTab,
      isSearch: trimmedQuery.length > 0,
      sortDir: isSortDesc ? "DESC" : "ASC",
      sortBy: sortBy,
      take: sessionContext?.IsWorkkardGridView ? 50 : 100,
      view: view,
      silent,
      _: +new Date(),
    };
    dispatch(fetchWorkkardListingDataRequest(workkardListingPayload));
  }, [sessionContext?.IsWorkkardGridView]);

  // A live workkard event elsewhere in the system refetches this listing with
  // the tab/sort/view arguments it currently owns -- silently, so the rows and
  // counts update in place instead of the page flashing a loader.
  useLiveRefresh(['workkard'], () => {
    fetchListingData({ silent: true });
    dispatch(fetchWorkkardTabCountRequest({ silent: true }));
  });

  const debouncedSearch = React.useMemo(
    () => debounce(() => fetchListingData(), 500),
    [],
  );

  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    dispatch(fetchWorkkardTabCountRequest());

    const values = queryString.parse(location.search);
    if (values.st) {
      setSelectedTab(values.st);
    } else {
      fetchListingData();
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const prevDialogueFlagRef = React.useRef(dialogueBox.flag);
  React.useEffect(() => {
    if (prevDialogueFlagRef.current === true && dialogueBox.flag === false) {
      dispatch(
        showDialogueBox({
          flag: false,
          title: null,
          dialogueBoxComponent: null,
        }),
      );
    }
    prevDialogueFlagRef.current = dialogueBox.flag;
  }, [dialogueBox.flag]);

  const prevAppErrorStatusRef = React.useRef(appError.status);
  React.useEffect(() => {
    if (appError.status && appError.status !== prevAppErrorStatusRef.current) {
      if (appError.status == 105) {
        window.location.reload();
      } else if (appError.isErrorScreen) {
        dispatch(
          showDialogueBox({
            flag: true,
            component: (
              <ErrorScreen status={appError.status} history={history} />
            ),
            overlayClassName: "modal animated faster fadeIn",
            contentClassName:
              "modalContent globalSearchModalContent modalFixed animated faster fadeInUpx2 medium",
            shouldCloseOnOverlayClick: true,
          }),
        );
      }
    }
    prevAppErrorStatusRef.current = appError.status;
  }, [appError]);

  const prevIsGridViewRef = React.useRef(sessionContext.IsWorkkardGridView);
  React.useEffect(() => {
    if (
      prevIsGridViewRef.current !== undefined &&
      prevIsGridViewRef.current !== sessionContext.IsWorkkardGridView
    ) {
      setView(resolveWorkkardListingView(sessionContext.IsWorkkardGridView));
    }
    prevIsGridViewRef.current = sessionContext.IsWorkkardGridView;
  }, [sessionContext.IsWorkkardGridView]);

  React.useEffect(() => {
    fetchListingData();
  }, [selectedTab, view, sortBy, isSortDesc]);

  const onTabClick = (selectedtab) => {
    setSelectedTab(selectedtab);
    setSearchText("");
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleSorting = (value) => {
    setSortBy(value);
    setIsSortDesc(true);
  };

  const toggleSortingDir = (value, newIsSortDesc) => {
    setIsSortDesc(
      typeof newIsSortDesc !== "undefined" ? newIsSortDesc : (prev) => !prev,
    );
    if (value) setSortBy(value);
  };

  const SCROLL_PAGE_SIZE = 100;

  const onScrollHandler = () => {
    if (isFetchingListing) return;

    const { page, total, workkardsFullData } = workkardListingData;
    const { selectedTab, sortBy, isSortDesc } = stateRef.current;
    const loadedCount = workkardsFullData?.length ?? 0;
    if (loadedCount >= total) return;

    const trimmedQuery = stateRef.current.searchText.toLocaleLowerCase().trim();
    const nextTake = Math.min(SCROLL_PAGE_SIZE, total - loadedCount);

    const payload = {
      key: trimmedQuery,
      skip: loadedCount,
      take: nextTake,
      rows: loadedCount,
      page: page + 1,
      status: selectedTab,
      isSearch: trimmedQuery.length > 0,
      sortDir: isSortDesc ? "DESC" : "ASC",
      sortBy: sortBy,
      view: stateRef.current.view,
      isScroll: true,
      _: +new Date(),
    };
    dispatch(fetchWorkkardListingDataRequest(payload));
  };

  const sortOptions = [
    { label: "Created date", value: 1 },
    { label: "Workkard number", value: 2 },
  ];

  return (
    <div className="content">
      <div className="detailPage">
        {isLoading && sessionContext && userView && <LoaderComponent />}
        {sessionContext && userView && (
          <CardListing
            type={ListingViewConstants.Workkard}
            data={{
              ...workkardListingData,
              WorkkardsFullData: workkardListingData.workkardsFullData,
              Total: workkardListingData.total,
            }}
            view={view}
            pagingModel={pagingModel}
            onScrollHandler={onScrollHandler}
            tabCounts={workkardListingTabs}
            selectedTab={selectedTab}
            onTabClick={onTabClick}
            sortOptions={sortOptions}
            onChangeSearchQuery={(e) => setSearchText(e.target.value)}
            search={debouncedSearch}
            sortBy={sortBy}
            handleSorting={handleSorting}
            isSortDesc={isSortDesc}
            toggleSortingDir={toggleSortingDir}
            handleViewChange={handleViewChange}
            searchText={searchText.toLocaleLowerCase()}
          />
        )}
      </div>
    </div>
  );
};

export default WorkkardListing;
